<script setup>
import {onMounted, onUnmounted} from "vue";
import PageHeader from '@/components/parts/PageHeader.vue';
import TutorialBoard from '@/components/parts/TutorialBoard.vue';
import {setCSSVariable} from '@/composables/challenges';
import tutorialExamples from '@/data/tutorial-examples.json';

const version = process.env.APP_VERSION;

const TUTORIAL_GRID = 4;
let savedRowAmount, savedColAmount;

onMounted(() => {
  const root = getComputedStyle(document.documentElement);
  savedRowAmount = root.getPropertyValue('--rowAmount').trim();
  savedColAmount = root.getPropertyValue('--colAmount').trim();
  setCSSVariable('--rowAmount', TUTORIAL_GRID);
  setCSSVariable('--colAmount', TUTORIAL_GRID);
});

onUnmounted(() => {
  if (savedRowAmount) setCSSVariable('--rowAmount', savedRowAmount);
  if (savedColAmount) setCSSVariable('--colAmount', savedColAmount);
});

function getExample(sentence, id) {
  if (id) return tutorialExamples.find(e => e.id === id);
  return tutorialExamples.find(e => e.sentence === sentence);
}

const sections = [
  {
    id: 'predicates',
    title: 'What is a predicate?',
    examples: [
      {sentence: '∃x Red(x)', caption: 'There exists at least one red shape.'},
    ],
  },
  {
    id: 'quantifiers',
    title: 'Quantifiers',
    examples: [
      {sentence: '∃x Blue(x)', caption: 'True: at least one shape is blue — one is enough.'},
      {sentence: '∀x Blue(x)', caption: 'False on this same board: not every shape is blue.'},
      {sentence: '∃x Large(x)', caption: 'At least one shape is large.'},
    ],
  },
  {
    id: 'connectives',
    title: 'Logical connectives',
    examples: [
      {sentence: '∃x (Red(x) ∧ Triangle(x))', caption: 'There exists a shape that is both red and a triangle.'},
      {sentence: '∀x (Square(x) → Blue(x))', caption: 'True: every square here happens to be blue.'},
      {
        sentence: '∀x (Square(x) → Blue(x))',
        id: 'implication-false',
        caption: 'False: one square (top row) is red, not blue.',
      },
      {
        sentence: '∀x (Square(x) → Blue(x))',
        id: 'implication-vacuous',
        caption: 'Vacuously true: there are no squares on this board, so the implication can\'t be violated.',
      },
      {sentence: '∃x ¬Small(x)', caption: 'There exists a shape that is not small.'},
    ],
  },
  {
    id: 'relations',
    title: 'Relations between shapes',
    examples: [
      {sentence: '∃x ∃y (Red(x) ∧ Blue(y) ∧ SameSize(x,y))', caption: 'There is a red shape and a blue shape of the same size.'},
      {sentence: 'Equals(c,c)', caption: 'True: c trivially equals itself — the same shape on the board.'},
      {sentence: 'NotEquals(c,d)', caption: 'True: c and d are distinct shapes.'},
    ],
  },
  {
    id: 'membership',
    title: 'Membership',
    examples: [
      {sentence: '∀x ∈ BottomHalf, Below(x,c)', caption: 'Every member of the bottom half is below shape c.'},
      {sentence: '∃x ∉ RightHalf, Blue(x)', caption: 'There is a blue shape that is not a member of the right half.'},
    ],
  },
  {
    id: 'position',
    title: 'Position predicates',
    examples: [
      {sentence: '∀x (Red(x) → TopHalf(x))', caption: 'Every red shape is in the top half of the board.'},
      {sentence: '∃x ∃y (Triangle(x) ∧ Square(y) ∧ LeftOf(x,y))', caption: 'There is a triangle to the left of a square.'},
      {sentence: '∃x ∃y ∃z (Between(y,x,z) ∧ Red(x) ∧ Blue(z))', caption: 'There is a shape between a red shape and a blue shape.'},
    ],
  },
];
</script>

<template>
  <PageHeader eyebrow="Intro" title="Intro"
              description="What Tarski is and how predicate logic works."/>
  <div class="tutorial-text">
    <div class="tutorial-shapes" aria-hidden="true">
      <svg viewBox="-50 -50 100 100" class="tutorial-shape tutorial-shape--triangle">
        <polygon points="-40,35 0,-35 40,35"/>
      </svg>
      <svg viewBox="-50 -50 100 100" class="tutorial-shape tutorial-shape--hexagon">
        <polygon points="-20,36 -40,0 -20,-36 20,-36 40,0 20,36"/>
      </svg>
      <svg viewBox="-50 -50 100 100" class="tutorial-shape tutorial-shape--square">
        <polygon points="-35,35 35,35 35,-35 -35,-35"/>
      </svg>
    </div>

    <section class="tutorial-section tutorial-section--intro">
      <p>Inspired by <a href="https://en.wikipedia.org/wiki/Tarski%27s_World" target="_blank" rel="noopener noreferrer">"Tarski's World"</a>,
        Tarski lets you see predicate logic in action. A statement like <code class="predicate">∀x Red(x)</code> becomes
        something you can check against a grid of colored shapes — triangles, squares, and hexagons.</p>
      <p>In
        <router-link to="/explore">Explore mode</router-link>,
        you write a predicate and the app generates a matching world of shapes.
        Switch between a syntax tree breakdown and a visual interpretation to understand how the logic maps to the grid.
      </p>
      <p>In
        <router-link to="/">Play mode</router-link>,
        the app generates random predicates and worlds for you.
        Your task: decide if each statement is true or false given the shapes on the board.
        Each correct answer earns points, with a speed bonus for faster responses.
        Higher complexity multiplies your score — and wrong answers cost you points.
      </p>
    </section>

    <!-- Section 1: Predicates -->
    <section class="tutorial-section">
      <h2 class="tutorial-section__title">{{ sections[0].title }}</h2>
      <p>A predicate is a property that is either <strong>true</strong> or <strong>false</strong>
        for a given object. In Tarski, the objects are colored geometric shapes on a grid,
        and predicates describe their properties.</p>
      <p>For example, <code class="predicate">Red(x)</code> is true when shape
        <code class="predicate">x</code> is red.
        <code class="predicate">Triangle(x)</code> is true when <code class="predicate">x</code> is a triangle.
        <code class="predicate">Small(x)</code> is true when <code class="predicate">x</code> is small.</p>
      <p>These are <em>unary</em> predicates — they take one argument and check a single property.</p>
      <p><code class="predicate">x</code>, <code class="predicate">y</code>, and <code class="predicate">z</code>
        are <em>variables</em> — placeholders for "some shape," bound by a quantifier (below) that says how many
        shapes they can stand for. Any other letter is a <em>constant</em>, naming one specific shape on the board.
        <code class="predicate">Red(c)</code> asks whether the shape named <code class="predicate">c</code> is red;
        <code class="predicate">∃x Red(x)</code> asks whether any shape at all is red.</p>

      <div class="tutorial-ref">
        <h3 class="tutorial-ref__heading">Available predicates</h3>
        <div class="tutorial-ref__grid">
          <div class="tutorial-ref__group">
            <span class="tutorial-ref__label">Color</span>
            <code class="predicate">Red</code>
            <code class="predicate">Yellow</code>
            <code class="predicate">Green</code>
            <code class="predicate">Blue</code>
          </div>
          <div class="tutorial-ref__group">
            <span class="tutorial-ref__label">Shape</span>
            <code class="predicate">Triangle</code>
            <code class="predicate">Square</code>
            <code class="predicate">Hexagon</code>
          </div>
          <div class="tutorial-ref__group">
            <span class="tutorial-ref__label">Size</span>
            <code class="predicate">Small</code>
            <code class="predicate">Medium</code>
            <code class="predicate">Large</code>
          </div>
        </div>
      </div>

      <TutorialBoard v-for="ex in sections[0].examples" :key="ex.id ?? ex.sentence"
                     :segments="getExample(ex.sentence, ex.id)?.segments ?? []"
                     :sentence="ex.sentence"
                     :human-readable="getExample(ex.sentence, ex.id)?.humanReadable"
                     :caption="ex.caption"/>
    </section>

    <!-- Section 2: Quantifiers -->
    <section class="tutorial-section">
      <h2 class="tutorial-section__title">{{ sections[1].title }}</h2>
      <p>A predicate alone doesn't form a statement — you need a <em>quantifier</em>
        to say <em>how many</em> shapes must satisfy it.</p>
      <p><code class="predicate">∀</code> means <strong>"for all"</strong> —
        every shape on the board must satisfy the predicate.</p>
      <p><code class="predicate">∃</code> means <strong>"there exists"</strong> —
        at least one shape must satisfy it.</p>
      <p>The same board can make <code class="predicate">∃x Blue(x)</code> true
        (one blue shape is enough) while <code class="predicate">∀x Blue(x)</code>
        is false (not every shape is blue).</p>

      <TutorialBoard v-for="ex in sections[1].examples" :key="ex.id ?? ex.sentence"
                     :segments="getExample(ex.sentence, ex.id)?.segments ?? []"
                     :sentence="ex.sentence"
                     :human-readable="getExample(ex.sentence, ex.id)?.humanReadable"
                     :caption="ex.caption"/>
    </section>

    <!-- Section 3: Connectives -->
    <section class="tutorial-section">
      <h2 class="tutorial-section__title">{{ sections[2].title }}</h2>
      <p>You can combine predicates using logical connectives:</p>
      <div class="tutorial-ref">
        <div class="tutorial-ref__grid tutorial-ref__grid--connectives">
          <div class="tutorial-ref__group">
            <code class="predicate">∧</code>
            <span class="tutorial-ref__desc">and — both must be true</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">∨</code>
            <span class="tutorial-ref__desc">or — at least one must be true</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">¬</code>
            <span class="tutorial-ref__desc">not — inverts the truth value</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">→︎</code>
            <span class="tutorial-ref__desc">implies — if the left is true, then the right must be</span>
          </div>
        </div>
      </div>

      <TutorialBoard v-for="ex in sections[2].examples" :key="ex.id ?? ex.sentence"
                     :segments="getExample(ex.sentence, ex.id)?.segments ?? []"
                     :sentence="ex.sentence"
                     :human-readable="getExample(ex.sentence, ex.id)?.humanReadable"
                     :caption="ex.caption"/>
    </section>

    <!-- Section 4: Relations -->
    <section class="tutorial-section">
      <h2 class="tutorial-section__title">{{ sections[3].title }}</h2>
      <p>Some predicates compare <em>two</em> shapes. These are <em>binary</em> predicates —
        they take two arguments.</p>
      <p><code class="predicate">Larger(x, y)</code> is true when
        <code class="predicate">x</code> is larger than <code class="predicate">y</code>.
        <code class="predicate">SameSize(x, y)</code> is true when both are the same size.</p>
      <p><code class="predicate">Equals(x, y)</code> is true when <code class="predicate">x</code> and
        <code class="predicate">y</code> refer to the <em>same</em> shape on the board — not a shape that merely
        looks the same. Two different shapes that happen to share color, size, and form still make
        <code class="predicate">Equals(x, y)</code> false. <code class="predicate">NotEquals(x, y)</code> is
        true whenever <code class="predicate">x</code> and <code class="predicate">y</code> are two distinct
        shapes.</p>
      <p>With two variables, you often need two quantifiers.
        <code class="predicate">∃x ∃y</code> means "there exist two shapes such that…"
        and <code class="predicate">∀x ∃y</code> means "for every shape x, there is some shape y such that…"</p>

      <div class="tutorial-ref">
        <h3 class="tutorial-ref__heading">Binary predicates</h3>
        <div class="tutorial-ref__grid">
          <div class="tutorial-ref__group">
            <span class="tutorial-ref__label">Size</span>
            <code class="predicate">Smaller</code>
            <code class="predicate">Larger</code>
            <code class="predicate">SameSize</code>
          </div>
          <div class="tutorial-ref__group">
            <span class="tutorial-ref__label">Equality</span>
            <code class="predicate">Equals</code>
            <code class="predicate">NotEquals</code>
          </div>
        </div>
      </div>

      <TutorialBoard v-for="ex in sections[3].examples" :key="ex.id ?? ex.sentence"
                     :segments="getExample(ex.sentence, ex.id)?.segments ?? []"
                     :sentence="ex.sentence"
                     :human-readable="getExample(ex.sentence, ex.id)?.humanReadable"
                     :caption="ex.caption"/>
    </section>

    <!-- Section 5: Membership -->
    <section class="tutorial-section">
      <h2 class="tutorial-section__title">{{ sections[4].title }}</h2>
      <p>Instead of quantifying over the whole board, you can restrict a quantifier to a named group of
        shapes using <code class="predicate">∈</code>, meaning <strong>"is a member of,"</strong> or its
        opposite, <code class="predicate">∉</code>.</p>
      <p><code class="predicate">∀x ∈ BottomHalf, Below(x, c)</code> reads as: for every shape
        <code class="predicate">x</code> in the bottom half, <code class="predicate">x</code> is below
        <code class="predicate">c</code>. Only shapes in the bottom half are checked, not the whole board.</p>
      <p>The group after <code class="predicate">∈</code> can be any unary predicate — a color, a shape, or a
        position like <code class="predicate">BottomHalf</code>.</p>

      <TutorialBoard v-for="ex in sections[4].examples" :key="ex.id ?? ex.sentence"
                     :segments="getExample(ex.sentence, ex.id)?.segments ?? []"
                     :sentence="ex.sentence"
                     :human-readable="getExample(ex.sentence, ex.id)?.humanReadable"
                     :caption="ex.caption"/>
    </section>

    <!-- Section 6: Position -->
    <section class="tutorial-section">
      <h2 class="tutorial-section__title">{{ sections[5].title }}</h2>
      <p>Position predicates describe where shapes sit on the grid.</p>
      <p>Unary position predicates check which half of the board a shape is in:</p>

      <div class="tutorial-ref">
        <div class="tutorial-ref__grid tutorial-ref__grid--connectives">
          <div class="tutorial-ref__group">
            <code class="predicate">TopHalf(x)</code>
            <span class="tutorial-ref__desc">x is in the top half</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">BottomHalf(x)</code>
            <span class="tutorial-ref__desc">x is in the bottom half</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">LeftHalf(x)</code>
            <span class="tutorial-ref__desc">x is in the left half</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">RightHalf(x)</code>
            <span class="tutorial-ref__desc">x is in the right half</span>
          </div>
        </div>
      </div>

      <p>Binary position predicates compare two shapes:</p>

      <div class="tutorial-ref">
        <div class="tutorial-ref__grid tutorial-ref__grid--connectives">
          <div class="tutorial-ref__group">
            <code class="predicate">SameCol(x, y)</code>
            <span class="tutorial-ref__desc">x is in the same column as y</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">SameRow(x, y)</code>
            <span class="tutorial-ref__desc">x is in the same row as y</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">LeftOf(x, y)</code>
            <span class="tutorial-ref__desc">x is left of y</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">Above(x, y)</code>
            <span class="tutorial-ref__desc">x is above y</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">Below(x, y)</code>
            <span class="tutorial-ref__desc">x is below y</span>
          </div>
          <div class="tutorial-ref__group">
            <code class="predicate">MirrorCol(x, y)</code>
            <span class="tutorial-ref__desc">x mirrors y's column across the board's center</span>
          </div>
        </div>
      </div>

      <p>There is also <code class="predicate">Between(x, y, z)</code> — a ternary predicate
        that is true when <code class="predicate">x</code> is between
        <code class="predicate">y</code> and <code class="predicate">z</code>.</p>

      <TutorialBoard v-for="ex in sections[5].examples" :key="ex.id ?? ex.sentence"
                     :segments="getExample(ex.sentence, ex.id)?.segments ?? []"
                     :sentence="ex.sentence"
                     :human-readable="getExample(ex.sentence, ex.id)?.humanReadable"
                     :caption="ex.caption"/>

      <div class="tutorial-cta">
        <router-link to="/explore" class="black-button">Try writing your own sentences</router-link>
      </div>
    </section>

    <p class="tutorial-version">{{ version }}</p>
  </div>
</template>

<style scoped>
.tutorial-text {
  position: relative;
  padding: 0 0 40px;
  text-align: left;
  max-width: 65ch;
  line-height: 1.6;
  text-wrap: pretty;
}

.tutorial-shapes {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.tutorial-shape {
  position: absolute;
  opacity: 0.04;
  fill: none;
  stroke: var(--tomorrows-harvest);
  stroke-width: 2;
}

.tutorial-shape--triangle {
  width: 140px;
  height: 140px;
  top: 60px;
  right: -30px;
  transform: rotate(15deg);
}

.tutorial-shape--hexagon {
  width: 90px;
  height: 90px;
  bottom: 120px;
  right: 20px;
  transform: rotate(-10deg);
}

.tutorial-shape--square {
  width: 70px;
  height: 70px;
  top: 50%;
  right: -10px;
  transform: rotate(18deg);
}

.tutorial-section {
  padding: 32px 0;
  border-top: 1px solid var(--border-color);
}

.tutorial-section--intro {
  border-top: none;
  padding-top: 8px;
}

.tutorial-section--intro + .tutorial-section {
  border-top: 1px solid var(--border-color);
}

.tutorial-section__title {
  font-size: clamp(20px, 4vw, 28px);
  font-weight: 700;
  color: var(--text-color-bright);
  letter-spacing: -0.02em;
  margin: 0 0 16px;
  line-height: 1.1;
  text-wrap: balance;
}

.tutorial-section p {
  margin: 0 0 12px;
}

.tutorial-ref {
  margin: 20px 0;
  padding: 16px 20px;
  border: 1px solid var(--border-color);
  background: var(--nav-background);
}

.tutorial-ref__heading {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-color-bright);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0 0 12px;
}

.tutorial-ref__grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tutorial-ref__group {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px 10px;
}

.tutorial-ref__label {
  font-size: 12px;
  color: var(--text-color);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  min-width: 70px;
}

.tutorial-ref__grid--connectives .tutorial-ref__group {
  gap: 6px 8px;
}

.tutorial-ref__desc {
  font-size: 13px;
  color: var(--text-color);
}

.predicate {
  font-family: var(--font), monospace;
  color: var(--tomorrows-harvest);
  background: none;
  padding: 0;
  font-size: inherit;
}

.tutorial-cta {
  margin-top: 32px;
}

@media screen and (max-width: 639px) {
  .tutorial-section {
    padding: 24px 0;
  }

  .tutorial-ref {
    padding: 12px 16px;
  }

  .tutorial-ref__label {
    min-width: auto;
    width: 100%;
  }

  .tutorial-cta .black-button {
    width: 100%;
  }
}

.tutorial-version {
  margin-top: 2rem;
  font-size: 0.7rem;
  color: var(--border-color-bright);
  text-align: left;
}

.tutorial-text a {
  text-decoration-line: underline;
  text-decoration-color: var(--border-color);
  text-underline-offset: 3px;
  transition: text-decoration-color 0.15s;
}

.tutorial-text a:hover {
  text-decoration-color: var(--text-color-bright);
}

.tutorial-text a[target="_blank"]::after {
  content: " \2197\FE0E";
  font-size: 0.75em;
}

@media (prefers-reduced-motion: reduce) {
  .tutorial-shape {
    opacity: 0.03;
  }
}
</style>